<!-- src\components\course\CertificateForm.vue -->
<template>
	<div class="certificate-form">
		<div class="certificate-form__header">
			<h3 class="certificate-form__title">
				{{ isEdit ? 'Редактирование шаблона' : 'Создание шаблона' }}
			</h3>
			<button
				class="certificate-form__close-btn"
				type="button"
				@click="handleCancel"
				title="Закрыть"
			>
				✕
			</button>
		</div>

		<form class="certificate-form__form" @submit.prevent="handleSubmit">
			<div class="certificate-form__field">
				<label class="certificate-form__label" for="certificate-template-name">
					Название шаблона <span class="required">*</span>
				</label>
				<input
					id="certificate-template-name"
					v-model="formData.template_name"
					class="certificate-form__input"
					type="text"
					placeholder="Например: Сертификат о прохождении курса"
					required
				/>
			</div>

			<div class="certificate-form__field">
				<label class="certificate-form__label" for="certificate-template-content">
					Содержание сертификата <span class="required">*</span>
				</label>
				<textarea
					id="certificate-template-content"
					v-model="formData.template_content"
					class="certificate-form__textarea certificate-form__textarea--large"
					placeholder="Используйте переменные: {student_name}, {course_name}, {completion_date}, {score}"
					rows="8"
					required
				></textarea>
				<p class="certificate-form__hint">
					Доступные переменные: {student_name}, {course_name}, {completion_date},
					{score}
				</p>
			</div>

			<div class="certificate-form__field">
				<label class="certificate-form__label">
					Критерии получения <span class="required">*</span>
				</label>
				<div class="certificate-form__criteria">
					<div
						v-for="(criterion, index) in formData.criteria"
						:key="index"
						class="certificate-form__criterion-item"
					>
						<input
							v-model="formData.criteria[index]"
							class="certificate-form__criterion-input"
							type="text"
							placeholder="Например: Пройти все уроки"
							required
						/>
						<button
							class="certificate-form__criterion-remove"
							type="button"
							@click="removeCriterion(index)"
							title="Удалить"
							:disabled="formData.criteria.length <= 1"
						>
							✕
						</button>
					</div>
				</div>
				<button
					class="certificate-form__add-criterion-btn"
					type="button"
					@click="addCriterion"
				>
					<span class="certificate-form__add-icon">+</span>
					Добавить критерий
				</button>
			</div>

			<div class="certificate-form__actions">
				<button
					class="certificate-form__btn certificate-form__btn--cancel"
					type="button"
					@click="handleCancel"
					:disabled="saving"
				>
					Отменить
				</button>
				<button
					class="certificate-form__btn certificate-form__btn--submit"
					type="submit"
					:disabled="saving"
				>
					{{ saving ? 'Сохранение...' : isEdit ? 'Обновить' : 'Создать' }}
				</button>
			</div>
		</form>
	</div>
</template>

<script>
export default {
	name: 'CertificateForm',
	props: {
		certificate: {
			type: Object,
			default: null,
		},
		saving: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			formData: {
				template_name: '',
				template_content: '',
				criteria: [''],
			},
		}
	},
	computed: {
		isEdit() {
			return !!this.certificate
		},
	},
	mounted() {
		if (this.certificate) {
			let criteria = ['']
			if (this.certificate.criteria) {
				try {
					criteria =
						typeof this.certificate.criteria === 'string'
							? JSON.parse(this.certificate.criteria)
							: this.certificate.criteria
					if (!Array.isArray(criteria)) criteria = ['']
				} catch (e) {
					criteria = ['']
				}
			}

			this.formData = {
				template_name: this.certificate.template_name || '',
				template_content: this.certificate.template_content || '',
				criteria: criteria,
			}
		}
	},
	methods: {
		addCriterion() {
			this.formData.criteria.push('')
		},

		removeCriterion(index) {
			if (this.formData.criteria.length > 1) {
				this.formData.criteria.splice(index, 1)
			}
		},

		handleSubmit() {
			if (!this.formData.template_name.trim()) {
				alert('Пожалуйста, введите название шаблона')
				return
			}

			if (!this.formData.template_content.trim()) {
				alert('Пожалуйста, введите содержание сертификата')
				return
			}

			const hasEmptyCriteria = this.formData.criteria.some(c => !c.trim())
			if (hasEmptyCriteria) {
				alert('Пожалуйста, заполните все критерии')
				return
			}

			this.$emit('submit', { ...this.formData })
		},

		handleCancel() {
			if (
				confirm(
					'Вы уверены, что хотите отменить? Все несохраненные данные будут потеряны.'
				)
			) {
				this.$emit('cancel')
			}
		},
	},
}
</script>

<style scoped>
.certificate-form {
	background-color: #ffffff;
	border-radius: 12px;
	padding: 24px;
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
	max-height: 80vh;
	overflow-y: auto;
}

.certificate-form__header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 24px;
	padding-bottom: 16px;
	border-bottom: 2px solid #e8e8e8;
}

.certificate-form__title {
	color: #252641;
	font-family: 'Poppins', Helvetica;
	font-size: 22px;
	font-weight: 600;
	margin: 0;
}

.certificate-form__close-btn {
	width: 32px;
	height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: transparent;
	border: none;
	border-radius: 50%;
	color: #636363;
	font-size: 24px;
	cursor: pointer;
	transition: all 0.2s ease;
}

.certificate-form__close-btn:hover {
	background-color: #f5f5f5;
	color: #dc3545;
}

.certificate-form__form {
	display: flex;
	flex-direction: column;
	gap: 20px;
}

.certificate-form__field {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.certificate-form__label {
	color: #252641;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	font-weight: 500;
}

.required {
	color: #dc3545;
}

.certificate-form__input,
.certificate-form__textarea {
	padding: 12px 16px;
	border: 2px solid #e8e8e8;
	border-radius: 8px;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	color: #252641;
	transition: all 0.2s ease;
}

.certificate-form__input:focus,
.certificate-form__textarea:focus {
	outline: none;
	border-color: #0095ec;
	box-shadow: 0 0 0 3px rgba(0, 149, 236, 0.1);
}

.certificate-form__textarea {
	resize: vertical;
	min-height: 100px;
}

.certificate-form__textarea--large {
	min-height: 200px;
}

.certificate-form__hint {
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: 12px;
	margin: 0;
}

.certificate-form__criteria {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.certificate-form__criterion-item {
	display: flex;
	align-items: center;
	gap: 10px;
}

.certificate-form__criterion-input {
	flex: 1;
	padding: 10px 14px;
	border: 2px solid #e8e8e8;
	border-radius: 8px;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	color: #252641;
	transition: all 0.2s ease;
}

.certificate-form__criterion-input:focus {
	outline: none;
	border-color: #0095ec;
	box-shadow: 0 0 0 3px rgba(0, 149, 236, 0.1);
}

.certificate-form__criterion-remove {
	width: 32px;
	height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: #f5f5f5;
	border: none;
	border-radius: 50%;
	color: #dc3545;
	font-size: 18px;
	cursor: pointer;
	transition: all 0.2s ease;
	flex-shrink: 0;
}

.certificate-form__criterion-remove:hover:not(:disabled) {
	background-color: #f8d7da;
}

.certificate-form__criterion-remove:disabled {
	opacity: 0.4;
	cursor: not-allowed;
}

.certificate-form__add-criterion-btn {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 10px 16px;
	border: 2px dashed #e8e8e8;
	border-radius: 8px;
	background-color: #f8f8f8;
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;
}

.certificate-form__add-criterion-btn:hover {
	border-color: #0095ec;
	background-color: #f0f8ff;
	color: #0095ec;
}

.certificate-form__add-icon {
	font-size: 18px;
}

.certificate-form__actions {
	display: flex;
	gap: 12px;
	justify-content: flex-end;
	margin-top: 8px;
	padding-top: 20px;
	border-top: 2px solid #e8e8e8;
}

.certificate-form__btn {
	padding: 12px 24px;
	border: none;
	border-radius: 8px;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;
}

.certificate-form__btn:disabled {
	opacity: 0.6;
	cursor: not-allowed;
}

.certificate-form__btn--cancel {
	background-color: #f5f5f5;
	color: #636363;
}

.certificate-form__btn--cancel:hover:not(:disabled) {
	background-color: #e8e8e8;
}

.certificate-form__btn--submit {
	background-color: #0095ec;
	color: #ffffff;
}

.certificate-form__btn--submit:hover:not(:disabled) {
	background-color: #0077be;
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(0, 149, 236, 0.3);
}
</style>

